import { useEffect, useState } from 'react';
import { authService } from '@/services/authService';
import { withOrgHeader } from '@/services/apiContext';

const MEDIA_API_BASE_URL = import.meta.env.VITE_VIDEO_API_BASE_URL || 'http://localhost:5001';

function isDirectPlayableUrl(url) {
  return typeof url === 'string' && (
    url.startsWith('blob:')
    || url.startsWith('data:')
  );
}

function normalizeMediaUrl(url) {
  if (typeof url !== 'string' || !url) return null;
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('blob:')
    || url.startsWith('data:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${MEDIA_API_BASE_URL}${url}`;
  }
  return url;
}

function isDirectMediaFallbackError(error) {
  const message = String(error?.message || '').toLowerCase();
  return (
    error instanceof TypeError
    || message.includes('failed to fetch')
    || message.includes('network error')
    || message.includes('load failed')
  );
}

export default function useAuthenticatedMediaUrl(url) {
  const normalizedUrl = normalizeMediaUrl(url);
  const [resolvedUrl, setResolvedUrl] = useState(
    isDirectPlayableUrl(normalizedUrl) ? normalizedUrl : null,
  );
  const [isLoading, setIsLoading] = useState(
    Boolean(normalizedUrl) && !isDirectPlayableUrl(normalizedUrl),
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!normalizedUrl) {
      setResolvedUrl(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    if (isDirectPlayableUrl(normalizedUrl)) {
      setResolvedUrl(normalizedUrl);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    const loadMedia = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(normalizedUrl, {
          credentials: 'include',
          cache: 'no-store',
          headers: withOrgHeader({}),
        });

        if (response.status === 401) {
          authService.handleUnauthorized();
          throw new Error('Unauthorized');
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        if (cancelled) return;

        objectUrl = window.URL.createObjectURL(blob);
        setResolvedUrl(objectUrl);
      } catch (nextError) {
        if (cancelled) return;
        if (isDirectMediaFallbackError(nextError)) {
          console.warn('Authenticated media fetch failed, falling back to direct media URL', nextError);
          setResolvedUrl(normalizedUrl);
          setError(null);
          return;
        }

        console.error('Failed to load authenticated media URL', nextError);
        setResolvedUrl(null);
        setError(nextError);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadMedia();

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [normalizedUrl]);

  return {
    resolvedUrl,
    isLoading,
    error,
  };
}
